import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Map, List, Record } from "immutable";
import SvgIcon from "components/Common/SvgIcon";
import Button from "components/Common/Button";
import SelectSearchDropdown from "components/Common/SelectSearchDropdown";
import DebouncedInput from "components/Common/DebouncedInput";
import { ScheduleRecord } from "components/Pages/Settings/SettingsSchedules/reducer";
import { closeModalAction } from "actions/modal";
import {
  handleUpdateScheduleModalAction,
  handleSaveScheduleModalAction,
  handleUpdateScheduleRowAction,
  handleAddScheduleRowAction,
  handleDeleteScheduleRowAction
} from "components/Pages/Settings/SettingsSchedules/actions";
import colors from "stylesheets/utilities/colors.scss";
import SettingsScheduleRow from "./SettingsScheduleRow";
import timezones from "./timezones";

import "./style.scss";

const BLOCK_NAME = "SettingsSchedulesModal";
const MODAL_ERROR_MESSAGE = "Resolve errors to save this schedule";
class SettingsSchedulesModal extends React.Component {
  /**
   * @param {Immutable.List} normalOperatingDaysRows
   * @returns {Boolean}
   */
  static normalOperatingHoursHasRules(normalOperatingDaysRows) {
    return normalOperatingDaysRows.size &&
    normalOperatingDaysRows.every(rule => rule.get("days").size);
  }

  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.state = {
      scheduleErrors: null
    };

    this.handleSaveSchedule = this.handleSaveSchedule.bind(this);
    this.handleUpdateNormalOperatingDaysRows = this.handleUpdateNormalOperatingDaysRows.bind(this);
    this.handleUpdateSpecialOperatingDaysRows = (
      this.handleUpdateSpecialOperatingDaysRows.bind(this)
    );
    this.handleDeleteNormalOperatingDaysRows = this.handleDeleteNormalOperatingDaysRows.bind(this);
    this.handleDeleteSpecialOperatingDaysRows = (
      this.handleDeleteSpecialOperatingDaysRows.bind(this)
    );
    this.getTimezoneDropdownTopOptions = this.getTimezoneDropdownTopOptions.bind(this);
    // Validation methods
    this.isScheduleInvalid = this.isScheduleInvalid.bind(this);
    this.scheduleNameHasConflicts = this.scheduleNameHasConflicts.bind(this);
  }

  /**
   * shouldComponentUpdate
   * @param {Object} nextProps
   * @param {Object} nextState
   * @returns {Boolean} should re-render on prop/state change
   */
  shouldComponentUpdate(nextProps, nextState) {
    const {
      schedule
    } = this.props;

    const {
      scheduleErrors
    } = this.state;

    return (
      schedule !== nextProps.schedule ||
      scheduleErrors !== nextState.scheduleErrors
    );
  }

  /**
   * Count up and return the top 3 most used timezones.
   * @returns {Array<String>}
   */
  getTimezoneDropdownTopOptions() {
    const {
      settingsScheduledBlockSchedules
    } = this.props;

    if (!settingsScheduledBlockSchedules) {
      return [];
    }

    // Create a memo of the number of occurences of each timezone
    // in the existing schedules
    let topOptions = settingsScheduledBlockSchedules
      .reduce((acc, schedule) => {
        let updatedMemo = acc;

        if (!updatedMemo.get(schedule.get("timezone"))) {
          updatedMemo = updatedMemo.set(
            schedule.get("timezone"),
            1
          );

          return updatedMemo;
        }

        updatedMemo = updatedMemo.set(
          schedule.get("timezone"),
          updatedMemo.get(schedule.get("timezone")) + 1
        );

        return updatedMemo;
      }, Map())
      .sort((timezoneACount, timezoneBCount) => (
        timezoneACount < timezoneBCount
      ));

    topOptions = topOptions
      .keySeq()
      .toList()
      .slice(0, 3)
      .map(timezoneString => timezones.find(
        timezone => timezone.value === timezoneString
      ) || timezoneString) // fallback to the string if it's not in the map
      .toJS();

    return topOptions;
  }

  /** */
  handleSaveSchedule() {
    const { handleSaveScheduleModal } = this.props;

    handleSaveScheduleModal();
  }

  /**
   * @param {Object} value
   * @param {String} scheduleType - "normalOperatingDaysRows" or "specialOperatingDaysRows"
   */
  handleUpdateRow(value, scheduleType) {
    const { handleUpdateScheduleRow } = this.props;

    handleUpdateScheduleRow({ ...value, scheduleType });
  }

  /**
   * @param {Object} value
   */
  handleUpdateNormalOperatingDaysRows(value) {
    this.handleUpdateRow(value, "normalOperatingDaysRows");
  }

  /**
   * @param {Object} value
   */
  handleUpdateSpecialOperatingDaysRows(value) {
    this.handleUpdateRow(value, "specialOperatingDaysRows");
  }

  /**
   * @param {Object} value
   * @param {String} scheduleType - "normalOperatingDaysRows" or "specialOperatingDaysRows"
   */
  handleDeleteRow(value, scheduleType) {
    const { handleDeleteScheduleRow } = this.props;

    handleDeleteScheduleRow({ ...value, scheduleType });
  }

  /**
   * @param {Object} value
   */
  handleDeleteNormalOperatingDaysRows(value) {
    this.handleDeleteRow(value, "normalOperatingDaysRows");
  }

  /**
   * @param {Object} value
   */
  handleDeleteSpecialOperatingDaysRows(value) {
    this.handleDeleteRow(value, "specialOperatingDaysRows");
  }

  /**
   * @returns {Boolean} A boolean that tells us if there is a conflict
   */
  scheduleNameHasConflicts() {
    const {
      settingsScheduledBlockSchedules,
      modal,
      schedule
    } = this.props;
    const currentModalIndex = modal.getIn(["modalProps", "scheduleIndex"]);

    if (!settingsScheduledBlockSchedules) {
      return false;
    }

    // If it is invalid, return the error message
    return settingsScheduledBlockSchedules.some((existingSchedule, index) => (
      // Don't want to compare against the schedule we're editing

      (index !== currentModalIndex) &&
        (existingSchedule.get("name") === schedule.get("name"))
    ));
  }

  /**
   * validates the schedule and returns an array of errors.
   * This is used to disable the publish button
   * @returns {Boolean}
   */
  isScheduleInvalid() {
    const {
      schedule
    } = this.props;

    return Boolean(
      !schedule.get("name") ||
      this.scheduleNameHasConflicts() ||
      !schedule.get("isValid")
    );
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      modal,
      schedule,
      handleUpdateScheduleModal,
      handleAddScheduleRow,
      closeModal
    } = this.props;

    const scheduleName = schedule.get("name");
    const normalOperatingDaysRows = schedule.get("normalOperatingDaysRows");
    const specialOperatingDaysRows = schedule.get("specialOperatingDaysRows");

    const timeZoneOptions = timezones;

    const scheduleHasErrors = schedule.get("hasErrors");

    return (
      <div className={`${BLOCK_NAME} Modal__modal`}>
        <h5 className="Modal__title">
          {modal.getIn(["modalProps", "newSchedule"]) ? "New Recurring Schedule" : `Edit ${scheduleName}`}
        </h5>
        <div className={`Modal__content ${BLOCK_NAME}__content`}>
          <div className={`${BLOCK_NAME}__section ${BLOCK_NAME}__name-timezone-section`}>
            <div className={`${BLOCK_NAME}__name-input`}>
              <h2 className={`g-form__block__label ${BLOCK_NAME}__name-input__label`}>Schedule Name</h2>
              <DebouncedInput
                placeholder="Type a name for this schedule"
                value={schedule.name}
                onChange={name => handleUpdateScheduleModal({ name })}
                isInvalid={this.scheduleNameHasConflicts()}
              />
              {this.scheduleNameHasConflicts() && (
                <div className="SettingsSchedulesModal__input__error-message">
                  This schedule name is already in use
                </div>
              )}
            </div>
            <div className={`${BLOCK_NAME}__timezone-selector`}>
              <h2 className={`g-form__block__label ${BLOCK_NAME}__name-input__label`}>Timezone</h2>
              <SelectSearchDropdown
                placeholder="Select timezone"
                placeholderIcon="Globe"
                topOptions={this.getTimezoneDropdownTopOptions()}
                options={timeZoneOptions}
                value={schedule.timezone}
                onChange={timezone => handleUpdateScheduleModal({ timezone })}
              />
            </div>
          </div>
          <div className={`${BLOCK_NAME}__section ${BLOCK_NAME}__repeat-every-section`}>
            <div className={`${BLOCK_NAME}__panel`}>
              <div className={`${BLOCK_NAME}__panel-header`}>
                <SvgIcon
                  customClassName={`${BLOCK_NAME}__panel-icon`}
                  icon="BrowserRefresh"
                  height={18}
                />
                <h6 className={`${BLOCK_NAME}__panel-title`}>Repeat every</h6>
              </div>
              <div className={`${BLOCK_NAME}__panel-content`}>
                {normalOperatingDaysRows.map((row, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={`normalOperating${index}`}>
                    <hr className={`${BLOCK_NAME}__panel-divider`} />
                    <SettingsScheduleRow
                      rowIndex={index}
                      row={row}
                      days={row.get("days").toJS()}
                      start={row.get("start")}
                      end={row.get("end")}
                      allDay={row.get("allDay")}
                      onChange={this.handleUpdateNormalOperatingDaysRows}
                      onDelete={this.handleDeleteNormalOperatingDaysRows}
                      hasExpectionDatePicker={false}
                      deleteButton={normalOperatingDaysRows.size !== 1}
                      datePicker={false}
                    />
                  </React.Fragment>
                ))}
              </div>

              <div className={`${BLOCK_NAME}__panel-footer`}>
                <hr className={`${BLOCK_NAME}__panel-divider`} />
                <button
                  className={`${BLOCK_NAME}__panel-footer__button`}
                  type="button"
                  onClick={() => handleAddScheduleRow({ scheduleType: "normalOperatingDaysRows" })}
                >
                  <SvgIcon
                    customClassName={`${BLOCK_NAME}__panel-icon`}
                    icon="Add"
                    height={18}
                  />
                  Add a new rule
                </button>
                <hr className={`${BLOCK_NAME}__panel-divider`} />
              </div>
            </div>
          </div>
          <div className={`${BLOCK_NAME}__section ${BLOCK_NAME}__exception-section`}>
            <div className={`${BLOCK_NAME}__panel`}>
              <h6 className={`${BLOCK_NAME}__panel-header`}>
                <SvgIcon
                  customClassName={`${BLOCK_NAME}__panel-icon`}
                  icon="No"
                  height={18}
                />
                <h6 className={`${BLOCK_NAME}__panel-title`}>Except&nbsp;
                  <span className={`${BLOCK_NAME}__panel-title ${BLOCK_NAME}__panel-title--greyed`}>
                    (Optional)
                  </span>
                </h6>
              </h6>
              <hr className={`${BLOCK_NAME}__panel-divider`} />
              <div className={`${BLOCK_NAME}__panel-content`}>
                {specialOperatingDaysRows.map((row, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={`specialOperating${index}`}>
                    <SettingsScheduleRow
                      rowIndex={index}
                      row={row}
                      date={row.get("date")}
                      start={row.get("start")}
                      end={row.get("end")}
                      allDay={row.get("allDay")}
                      onChange={this.handleUpdateSpecialOperatingDaysRows}
                      onDelete={this.handleDeleteSpecialOperatingDaysRows}
                      hasExpectionDatePicker
                      exceptionDate={row.get("date")}
                      deleteButton
                    />
                    <hr className={`${BLOCK_NAME}__panel-divider`} />
                  </React.Fragment>
                ))}
              </div>
              <div className={`${BLOCK_NAME}__panel-footer`}>
                <button
                  className={`${BLOCK_NAME}__panel-footer__button`}
                  type="button"
                  onClick={() => handleAddScheduleRow({ scheduleType: "specialOperatingDaysRows" })}
                >
                  <SvgIcon
                    customClassName={`${BLOCK_NAME}__panel-icon`}
                    icon="Add"
                    height={18}
                  />
                  {specialOperatingDaysRows.size > 0 ? "Add a new exception" : "Add an exception"}
                </button>
                <hr className={`${BLOCK_NAME}__panel-divider`} />
              </div>
            </div>
          </div>
        </div>
        <div className={`${BLOCK_NAME}__controls Modal__bottom`}>
          <Button
            customClassName={`${BLOCK_NAME}__button-cancel`}
            title="Cancel Schedule"
            text="Cancel"
            onClick={closeModal}
            light
          />
          <div className="g-warning Modal__bottom__warning">
            {scheduleHasErrors &&
            (
              <div className="SettingsSchedulesModal__errors" key="errors">
                <SvgIcon
                  icon="Warning"
                  customClassName="Modal__bottom__warning__icon"
                  fillColor={colors.colorUIWarn}
                />
                <div>
                  <p className="SettingsSchedulesModal__errors__description">
                    {!normalOperatingDaysRows.size
                      ? "Add at least 1 rule to save this schedule"
                      : MODAL_ERROR_MESSAGE}
                  </p>
                </div>
              </div>
            )}
          </div>
          <Button
            title="Save Schedule"
            text="Save"
            icon="Cloud"
            disabled={this.isScheduleInvalid()}
            onClick={this.handleSaveSchedule}
          />
        </div>
      </div>
    );
  }
}

SettingsSchedulesModal.isLarge = true;

SettingsSchedulesModal.propTypes = {
  settingsScheduledBlockSchedules: PropTypes.instanceOf(List).isRequired,
  schedule: PropTypes.instanceOf(ScheduleRecord).isRequired,
  modal: PropTypes.instanceOf(Record).isRequired,
  closeModal: PropTypes.func.isRequired,
  handleUpdateScheduleModal: PropTypes.func.isRequired,
  handleSaveScheduleModal: PropTypes.func.isRequired,
  handleUpdateScheduleRow: PropTypes.func.isRequired,
  handleAddScheduleRow: PropTypes.func.isRequired,
  handleDeleteScheduleRow: PropTypes.func.isRequired
};

/**
 * @param {Object} state
 * @returns {Object}
 */
function mapStateToProps(state) {
  const { settingsScheduledBlockSchedules, settingsScheduleModal, modal } = state;

  return {
    settingsScheduledBlockSchedules,
    schedule: settingsScheduleModal,
    modal
  };
}

/**
 * @param {Function} dispatch
 * @returns {Object}
 */
function mapDispatchToProps(dispatch) {
  return {
    closeModal: bindActionCreators(closeModalAction, dispatch),
    handleUpdateScheduleModal: bindActionCreators(handleUpdateScheduleModalAction, dispatch),
    handleSaveScheduleModal: bindActionCreators(handleSaveScheduleModalAction, dispatch),
    handleUpdateScheduleRow: bindActionCreators(handleUpdateScheduleRowAction, dispatch),
    handleAddScheduleRow: bindActionCreators(handleAddScheduleRowAction, dispatch),
    handleDeleteScheduleRow: bindActionCreators(handleDeleteScheduleRowAction, dispatch)
  };
}

const Connector = connect(mapStateToProps, mapDispatchToProps)(SettingsSchedulesModal);

export default Connector;
